import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login, checked } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (checked && user) return <Navigate to="/admin/leads" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate("/admin/leads");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <img src="/leadtech-logo.png" alt="LeadTech Connect" className="h-24 w-auto mx-auto mb-6" />
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#0891B2]">
            <Lock size={12} /> Admin Access
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-xl p-8 space-y-6"
        >
          <h1 className="font-heading font-bold text-2xl text-slate-900 dark:text-white tracking-tight">Sign in</h1>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500">Email</label>
            <input
              data-testid="login-email"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@leadtechconnect.com"
              className="line-input"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500">Password</label>
            <input
              data-testid="login-password"
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="line-input"
              autoComplete="current-password"
            />
          </div>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#007AFF] hover:bg-[#0056d3] disabled:opacity-60 text-white text-sm font-medium px-6 py-3 shadow-[0_4px_20px_rgba(0,122,255,0.25)] hover:shadow-[0_6px_28px_rgba(0,122,255,0.4)] transition-colors"
          >
            {submitting ? "Signing in…" : "Sign In"} <ArrowRight size={16} />
          </button>

          <div className="text-xs text-slate-500 dark:text-slate-500 text-center">
            <a href="/" data-testid="login-back-home" className="hover:text-slate-900">← Back to site</a>
          </div>
        </form>
      </div>
    </div>
  );
}
